import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

const FAQ = [
  {
    section: "Ordering & Payment",
    items: [
      {
        q: "How do I buy an original?",
        a: "Every available piece has a Buy Now button that opens Square's secure checkout. No account needed — just card or Apple Pay. If you'd rather have a conversation first, use the Inquire form on any artwork page and I'll respond within 5–7 days.",
      },
      {
        q: "Do you offer payment plans?",
        a: "Yes. Afterpay is available at checkout via Square for eligible orders, splitting the total across four interest-free payments. For originals above $5,000, contact the studio directly and I'll set up a bespoke installment plan (typically 3–6 months with a deposit).",
      },
      {
        q: "Can I place a hold on a piece while I think?",
        a: "For serious inquiries on originals above $2,500, the studio will honor a 48-hour courtesy hold. Email studio@josephgravesjr.com with the piece title.",
      },
      {
        q: "Do you accept trade-ins or offers?",
        a: "Not typically, but interior designers and hospitality buyers should apply to the Trade Program for confidential pricing on multi-piece acquisitions.",
      },
    ],
  },
  {
    section: "Shipping & Handling",
    items: [
      {
        q: "How is the work shipped?",
        a: "Small works (under 24\") ship in reinforced cardboard with corner protection and rigid foam. Medium works ship in custom-cut wooden crates. Large works (48\" and up) ship in reinforced wooden crates via specialty art shippers. All shipments are fully insured for the sale price.",
      },
      {
        q: "How long does shipping take?",
        a: "Domestic (US): 5–10 business days after payment clears. International: 10–21 business days. I'll email tracking as soon as the piece leaves the studio.",
      },
      {
        q: "Do you ship internationally?",
        a: "Yes — worldwide. International buyers are responsible for import duties and any local VAT. I'll provide a properly declared customs invoice.",
      },
      {
        q: "Can I pick up in person?",
        a: "Yes. Studio pickup is available by appointment in Denver, CO. Choose \"Studio Pickup\" at checkout or mention it in the inquiry form. Shipping cost is refunded.",
      },
    ],
  },
  {
    section: "Returns & Damage",
    items: [
      {
        q: "What's the return policy?",
        a: "Originals: All original artwork sales are final. Given the one-of-one nature and the risk of shipping damage on return, returns are only accepted if the piece arrives damaged. Prints: 14-day return window if unopened and unframed. Buyer covers return shipping.",
      },
      {
        q: "What if the piece arrives damaged?",
        a: "Document the damage with photos of the crate and the piece before unpacking further. Email studio@josephgravesjr.com within 48 hours of delivery. Fully insured shipments are replaced or refunded — no exceptions.",
      },
    ],
  },
  {
    section: "Authenticity & Provenance",
    items: [
      {
        q: "Do originals come with a Certificate of Authenticity?",
        a: "Yes. Every original ships with a signed Certificate of Authenticity documenting the title, year, medium, dimensions, series, and acquisition record. Keep it with the piece — it matters for insurance, resale, and provenance.",
      },
      {
        q: "How can I verify a Graves piece?",
        a: "Every canvas is signed on the reverse and cross-referenced against the studio archive. If you're buying secondhand and want authentication, email studio@josephgravesjr.com with photos of the front, back, and signature.",
      },
    ],
  },
  {
    section: "Commissions & Custom Work",
    items: [
      {
        q: "Do you take commissions?",
        a: "Yes — see the Commissions page. Timeline is typically 8–16 weeks from deposit. Pricing scales with size and complexity. I do not paint from reference photos of copyrighted work or celebrities without permission.",
      },
      {
        q: "Can I commission a piece in a specific series?",
        a: "Yes. The Black Western, Figurative Contemporary, and Abstract series are all open for commission. Bring a concept, dimensions, and the wall it will live on.",
      },
    ],
  },
  {
    section: "Prints & Editions",
    items: [
      {
        q: "Are prints signed and numbered?",
        a: "Signed limited-edition prints (available through the studio) are hand-signed and numbered on the front and edition-stamped on the reverse. Open-edition prints from Level Press are unsigned but reproduction-quality.",
      },
      {
        q: "What's the paper and ink?",
        a: "Signed limited editions: archival giclée on 300gsm Hahnemühle Photo Rag with Ultrachrome Pro inks — museum-grade, 100+ year longevity. Open editions vary — see the Level Press storefront.",
      },
    ],
  },
];

function Row({ q, a, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left py-5 md:py-6 flex items-center justify-between gap-6 hover:opacity-80 transition-opacity"
      >
        <span className="serif text-lg md:text-2xl leading-snug">{q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="pb-6 md:pb-8 text-[hsl(var(--ink-soft))] leading-relaxed max-w-3xl">
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQ_Page() {
  return (
    <div data-testid="faq-page">
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32">
          <p className="label mb-3">Studio FAQ</p>
          <h1 className="serif text-5xl md:text-7xl leading-[0.98] tracking-tight font-light max-w-4xl">
            The answers{" "}
            <span className="italic text-[hsl(var(--ink-soft))]">
              collectors ask before they buy.
            </span>
          </h1>
        </div>
      </section>

      {FAQ.map((section, si) => (
        <section
          key={section.section}
          className={si % 2 === 0 ? "" : "bg-[hsl(var(--surface))]"}
          data-testid={`faq-section-${section.section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-4">
              <p className="label mb-2">Section {String(si + 1).padStart(2, "0")}</p>
              <h2 className="serif text-3xl md:text-4xl tracking-tight leading-tight">
                {section.section}
              </h2>
            </div>
            <div className="md:col-span-8">
              {section.items.map((it, i) => (
                <Row
                  key={it.q}
                  q={it.q}
                  a={it.a}
                  defaultOpen={si === 0 && i === 0}
                  testid={`faq-q-${section.section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${i}`}
                />
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="border-t border-border bg-foreground text-background">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
          <p className="label !text-background/60 mb-4">Still have questions?</p>
          <p className="serif text-3xl md:text-4xl leading-tight">
            Email{" "}
            <a
              href="mailto:studio@josephgravesjr.com"
              className="italic underline underline-offset-4"
            >
              studio@josephgravesjr.com
            </a>
            .
            <br />
            <span className="text-background/60">
              Replies within 5–7 business days.
            </span>
          </p>
        </div>
      </section>
    </div>
  );
}
